import { submitImageForPayment } from "lib/common/endpoints/public";
import { Registrations } from "lib/common/types/registration";
import usePut from "lib/hooks/usePut";
import { useEffect, useRef, useState } from "react";
import { UploadSimple, PaperPlaneTilt } from "@phosphor-icons/react";
import { ButtonSpinner } from "../atomic/Spinner/Spinner";
import Image from "../atomic/Image";
import { message as _message } from "antd";

interface ImageUpload {
  data: { message: string };
}

export default function RegisterUploadImage({
  registration,
  refetch,
  isFree,
}: {
  registration: Registrations;
  refetch: () => {};
  isFree: boolean;
}) {
  const [files, setFiles] = useState<File>();
  const uploadRef = useRef<HTMLInputElement>(null);
  const {
    data,
    put: uploadImage,
    error,
    isLoading,
  } = usePut<ImageUpload>(submitImageForPayment + registration?._id.$oid);

  const [image, setImage] = useState("");
  const handleFileUpload = () => {
    uploadRef.current && uploadRef.current.click();
  };
  useEffect(() => {
    if (error && error.hasOccurred) _message?.error(error.message);
  }, [error]);
  useEffect(() => {
    if (data) {
      _message?.success("Image Uploaded Successfully");
      refetch();
    }
  }, [data, refetch]);

  const handleFileChange = (e: any) => {
    e.preventDefault();
    let files;
    if (e.dataTransfer) {
      files = e.dataTransfer.files;
    } else if (e.target) {
      files = e.target.files;
    }
    setFiles(files[0]);
    const reader = new FileReader();
    reader?.readAsDataURL(files && files[0]);
    reader.onload = () => {
      setImage(reader.result as any);
    };
  };
  if (!registration)
    return (
      <div className="text-center text-sm text-gray-500 dark:text-gray-400">
        You need to fill up the registration form first.
      </div>
    );

  const uploadLabel = !isFree
    ? !registration.image
      ? "Upload payment receipt"
      : "Reupload payment receipt"
    : !registration.image
      ? "Upload identification document"
      : "Reupload identification document";

  return (
    <div className="space-y-3">
      {registration.image && !registration.isVerified && (
        <div className="rounded-xl border border-amber-200 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-950/20 px-4 py-3">
          <p className="text-xs font-semibold text-amber-800 dark:text-amber-300">
            Your payment is under review.
          </p>
          <p className="text-xs text-amber-600 dark:text-amber-500 mt-0.5 mb-2">
            You already submitted the image below — reupload only if it&apos;s wrong.
          </p>
          <Image
            src={registration.image}
            width={120}
            height={120}
            alt="Previously submitted proof"
            className="rounded-lg border border-amber-200 dark:border-amber-800/40"
          />
        </div>
      )}

      {image && (
        <div className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={image} className="w-16 h-16 object-cover rounded-lg shrink-0" alt="preview" />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">Ready to submit</p>
            <button
              type="button"
              onClick={() => {
                const formData = new FormData();
                formData.append("file", files as Blob);
                uploadImage(formData);
              }}
              disabled={isLoading}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-primary hover:bg-primary-600 text-white text-xs font-semibold transition-colors disabled:opacity-60"
            >
              <PaperPlaneTilt size={13} weight="fill" />
              Submit
              {isLoading && <ButtonSpinner />}
            </button>
          </div>
        </div>
      )}

      <input
        ref={uploadRef}
        className="opacity-0 h-0 w-0"
        type="file"
        name="image"
        accept="image/png, image/jpeg"
        id="image"
        onChange={handleFileChange}
      />
      <button
        type="button"
        onClick={handleFileUpload}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-semibold transition-colors"
      >
        <UploadSimple size={15} />
        {uploadLabel}
      </button>
    </div>
  );
}
